import { createSignal } from "solid-js";
import type { MeetingInsights } from "../types/insights";
import { saveMeetingInsights } from "../api/insights";
import { generateMeetingInsights } from "../api/meetingInsights";

// Factory hook: returns a new, isolated state container per invocation.
// Avoids cross-session bleeding caused by module-scoped signals.
export const useInsights = () => {
  const [insights, setInsights] = createSignal<MeetingInsights | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<string>("");
  const [summary, setSummary] = createSignal("");
  const [actionItems, setActionItems] = createSignal<
    Array<{ id: string; text: string; done: boolean }>
  >([]);

  function loadSavedInsights(
    data: Partial<MeetingInsights> | null | undefined
  ) {
    if (!data) return;
    setInsights(data as MeetingInsights);
    setSummary(data.summary ?? "");
    setActionItems((data.action_items as any) ?? []);
    setError("");
    setLoading(false);
  }

  async function fetchInsights(
    fullTranscript: string,
    contactType: string = "Intake",
    sessionId?: string
  ) {
    setLoading(true);
    setError("");
    try {
      const data = await generateMeetingInsights({
        transcript: fullTranscript,
        contactType,
      });
      setInsights(data);
      setSummary(data.summary ?? "");
      setActionItems(data.action_items ?? []);

      // Auto-save insights to database
      if (sessionId) {
        try {
          await saveMeetingInsights(sessionId, data);
          console.log("Auto-saved meeting insights");
        } catch (error) {
          console.error("Failed to auto-save insights:", error);
          // Don't throw error for auto-save failures
        }
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function markEntityVerified(id: string) {
    setInsights((i) =>
      i
        ? {
            ...i,
            entities: i.entities.map((e) =>
              e.id === id ? { ...e, verified: true } : e
            ),
          }
        : i
    );
  }

  function toggleAction(id: string) {
    setInsights((i) =>
      i
        ? {
            ...i,
            action_items: i.action_items.map((a) =>
              a.id === id ? { ...a, done: !a.done } : a
            ),
          }
        : i
    );
  }

  function reset() {
    setInsights(null);
    setSummary("");
    setActionItems([]);
    setError("");
    setLoading(false);
  }

  return {
    insights,
    summary,
    actionItems,
    loading: loading,
    error,
    loadSavedInsights,
    fetchInsights,
    markEntityVerified,
    toggleAction,
    reset,
  } as const;
};
